<script lang="ts">
import type { Nullable } from '@/types'
import { ThemeType } from '@goodlifeplus/themes'
import { type PropType, defineComponent } from 'vue'

/**
 * CarouselPagination
 *
 * Supports two modes:
 * - elements mode: pass HTMLElement[] for item-level pagination with windowing
 * - count mode: pass a number for page-level pagination (no windowing)
 */
export default defineComponent({
  props: {
    /**
     * The elements to paginate through (item-level mode).
     */
    elements: {
      type: Array as PropType<HTMLElement[]>,
      required: false,
    },
    /**
     * Number of pages to paginate through (page-level mode).
     * Use this instead of `elements` when paginating by page index.
     */
    count: {
      type: Number,
      required: false,
    },
    /**
     * The current index of the pagination.
     */
    currentIndex: {
      type: Number,
      default: 0,
    },
    /**
     * The number of bullets to show on either side of the current index.
     * Only applies in elements mode.
     */
    bulletCount: {
      type: Number,
      default: 2,
    },
  },
  emits: ['scrollTo'],
  data() {
    return {
      visibleBullets: [] as Array<HTMLElement>,
    }
  },
  computed: {
    isSubscription(): boolean {
      return this.$theme.type === ThemeType.Subscription
    },
    isCountMode(): boolean {
      return this.count !== undefined
    },
    currentBullet(): Nullable<HTMLElement> {
      if (this.isCountMode) {
        return null
      }
      return (
        this.visibleBullets.find((bullet) => {
          return bullet === this.elements![this.currentIndex]
        }) ?? null
      )
    },
    pageIndices(): number[] {
      return Array.from({ length: this.count ?? 0 }, (_, i) => i)
    },
  },
  watch: {
    currentIndex() {
      if (!this.isCountMode) {
        this.updateVisibleBullets()
      }
    },
  },
  mounted() {
    if (!this.isCountMode) {
      this.updateVisibleBullets()
    }
  },
  methods: {
    updateVisibleBullets() {
      const elements = this.elements ?? []
      const start = Math.max(
        Math.min(
          this.currentIndex > elements.length - this.bulletCount
            ? elements.length - this.bulletCount * 2
            : this.currentIndex - this.bulletCount
        ),
        0
      )
      const end = Math.min(
        this.currentIndex <= this.bulletCount
          ? this.bulletCount * 2 + 1
          : this.currentIndex + this.bulletCount * 2 - 1,
        elements.length
      )
      this.visibleBullets = elements.slice(start, end)
    },
    getElementIndex(element: HTMLElement): number {
      return (this.elements ?? []).indexOf(element)
    },
  },
})
</script>

<template>
  <!-- count mode: page-level pagination -->
  <div
    v-if="isCountMode"
    class="flex w-full items-center justify-center gap-1 mt-3"
  >
    <button
      v-for="i in pageIndices"
      :key="i"
      class="pagination-bullet h-2 transition-all duration-300"
      :class="
        i === currentIndex
          ? 'w-8 rounded-xl bg-primary'
          : 'w-6 rounded bg-[#ccc]'
      "
      :aria-label="`Go to page ${i + 1}`"
      :aria-current="i === currentIndex"
      @click="$emit('scrollTo', i)"
    />
  </div>

  <!-- elements mode: item-level pagination with windowing -->
  <div v-else class="mx-auto mb-4 mt-8 flex w-full justify-center gap-2">
    <div
      v-for="bullet in visibleBullets"
      :key="bullet.id"
      class="pagination-bullet size-4 rounded-full hover:cursor-pointer"
      :class="{
        'w-10 bg-tertiary': bullet === currentBullet,
        'bg-black/20': bullet !== currentBullet,
      }"
      :aria-label="`${getElementIndex(bullet)} / ${(elements ?? []).length - 1}`"
      :aria-current="bullet === currentBullet"
      @click="$emit('scrollTo', getElementIndex(bullet))"
    ></div>
  </div>
</template>

<style lang="scss" scoped>
.pagination-bullet {
  transition:
    all 0.3s ease-in-out,
    background-color 0.3s ease-in-out;
}
</style>
